import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as CloseIcon } from '../../assets/images/x.svg';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import {
  getHoursOutHistory,
  TimeOffHistory,
  TimeOffHistoryResponse,
} from '../../services/myTimeoff/MyTimeoffApi';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import { convertUTCtoLocalTimezone } from '../../utils/dateUtil';
import {
  LOCAL_DATE_TIME_UTC_FORMAT,
  LOCAL_DATE_TIME_FORMAT,
  LOCAL_TIME_12_HOUR_FORMAT,
} from '../../services/Constant';
import dayjs from 'dayjs';

interface HeaderData {
  type: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  duration: string;
  modifiedOn: string;
  modifiedBy: string;
}

export interface HoursOutHistoryProps {
  open: boolean;
  employeeId: string;
  date: string;
}

const tableHeadCells: HeadCell<HeaderData>[] = [
  {
    id: 'type',
    labelId: 'myTimeOffType',
    defaultLabelId: 'Type',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'startDate',
    labelId: 'startDateText',
    defaultLabelId: 'Start Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'endDate',
    labelId: 'endDateText',
    defaultLabelId: 'End Date',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'startTime',
    labelId: 'startTimeText',
    defaultLabelId: 'Start Time',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'duration',
    labelId: 'myTimeOffDuration',
    defaultLabelId: 'Duration',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiedOn',
    labelId: 'modifiedOnText',
    defaultLabelId: 'Modified On',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiedBy',
    labelId: 'modifiedByText',
    defaultLabelId: 'Modified By',
    numeric: false,
    requiredSorting: false,
  },
];

export interface HoursOutHistoryPopupProps {
  handleClose: () => void;
  open: boolean;
  employeeId: string;
  date: string;
  titleId: string;
  defaultMessage: string;
}

const HoursOutHostoryPopup = ({
  handleClose,
  open,
  employeeId,
  date,
  titleId,
  defaultMessage,
}: HoursOutHistoryPopupProps) => {
  const [timeOffHistory, setTimeOffHistory] = useState<
    TimeOffHistoryResponse[] | null
  >(null);
  const intl = useIntl();

  useEffect(() => {
    getHoursOutHistory(employeeId, date)
      .then((response) => {
        setTimeOffHistory(response.length > 0 ? response : []);
      })
      .catch(() => {
        setTimeOffHistory([]);
      });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle>
        <Grid container direction={'column'} rowSpacing={4}>
          <Grid item display={'flex'} className="mt-sm mb-sm">
            <MediumTypography
              className="mainText-lg"
              label={intl.formatMessage({
                id: titleId,
                defaultMessage: defaultMessage,
              })}
            />
            <MediumTypography
              className="mainText-md ml-sm"
              label={'- ' + date}
              sxProps={{ flex: 1 }}
            />
            <CloseIcon onClick={handleClose} className="enablePopupIcon" />
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHeader
              className="listDataTableHead"
              labelSxProp={{ whiteSpace: 'normal' }}
              headerNames={tableHeadCells}
              checkBoxRequired={false}
            />
            <TableBody className="tableRowcss">
              {timeOffHistory?.map((item, index) => (
                <>
                  {timeOffHistory?.length > 1 && (
                    <TableRow key={(index + 1).toString()}>
                      <TableCell colSpan={6}>
                        <MediumTypography
                          label={'Request ' + (index + 1)}
                          className={'mainText-lg font-bold'}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {item.latestTimeOff &&  item.timeOffHistory.every(obj => obj.isCreatedByRS === false ) && (
                    <TimeOffHistoryRow history={item.latestTimeOff} />
                  )}
                  {item.timeOffHistory?.map((history, i) => (
                    <TimeOffHistoryRow
                      history={history}
                      key={(i + 1).toString()}
                    />
                  ))}
                </>
              ))}
              {timeOffHistory?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <MediumTypography
                      className="flex__ justifyContent-Center"
                      labelid="noRecordsFoundMessage"
                      defaultlabel="No records found"
                    />
                  </TableCell>
                </TableRow>
              )}
              {timeOffHistory === null && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <MediumTypography
                      className="flex__ justifyContent-Center"
                      labelid="loadingText"
                      defaultlabel="Loading..."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

const TimeOffHistoryRow = ({ history }: { history: TimeOffHistory }) => {
  return (
    <TableRow>
      <TooltipTableCell
        visible={history.leaveName.length > 10}
        value={history.leaveName}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />
      <TooltipTableCell
        visible={false}
        value={dayjs(history.effectiveStartDate).format("MM/DD/YYYY")}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />
      <TooltipTableCell
        visible={false}
        value={dayjs(history.effectiveEndDate).format("MM/DD/YYYY")}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />
      <TooltipTableCell
        visible={false}
        value={convertUTCtoLocalTimezone(
          history.startDate ?? '',
          LOCAL_DATE_TIME_UTC_FORMAT,
          LOCAL_TIME_12_HOUR_FORMAT,
        )}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />

      <TooltipTableCell
        visible={false}
        value={history.noOfHrs ?? '-'}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />
      <TooltipTableCell
        visible={false}
        textSxProps={{
          maxWidth: '200px',
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
        value={convertUTCtoLocalTimezone(
          history.modifiedDate,
          LOCAL_DATE_TIME_UTC_FORMAT,
          LOCAL_DATE_TIME_FORMAT,
        )}
      />
      <TooltipTableCell
        visible={false}
        value={history.modifiedBy}
        textSxProps={{
          color: history.isDeleted
            ? 'var(--thom-radical-red)'
            : 'var(--thom-mono-black)',
        }}
      />
    </TableRow>
  );
};

export default HoursOutHostoryPopup;
